import { createSelector } from '@reduxjs/toolkit';
import {
  BaseField,
  getGroup,
  getPositionAmount,
  getPositionID,
  getTypedKeys,
  isCompound,
  isCounterCurrency,
  isSpot,
  PricingModeEnum,
  type QtyInputCurrencyOptions,
  SecurityTypesUtils,
} from '@talos/kyoko';
import type { AppState } from '../../../providers/AppStateProvider/types';
import { getOrdTypeForQuote, getRFQSyntheticCrossSymbol } from '../../../utils/rfq';
import type { GetRFQMarketSelectorItemsParams } from '../../MarketSelector/RFQ/getRFQMarketSelectorItems';
import { UNITS_CURRENCY } from '../CareOrderFormView/types';
import { subAccountFieldsToAllocations } from '../utils';

import { compact } from 'lodash-es';
import { streamingDataSlice } from '../../../providers/AppStateProvider/streamingDataSlice';
import { allocationsAreValid } from '../allocationUtils';
import type { AggregatePositionInfo } from '../NewOrder/components/PositionsTooltip';
import { getRFQFormCurrencySpec } from './currencies';
import type { RFQCurrency, RFQFormState } from './types';
import { isFalconXOption, isFundSelectableInRFQForm, RFQSLICE_POSITION_REQUEST_PARAMS } from './utils';

const selectCurrentCareOrder = (state: AppState) => state.rfq.currentCareOrder;
const selectIsLoading = (state: AppState) => state.rfq.isLoading;

/**
 * Check if all principal fields are valid
 * @param form - order form state
 * @param excludedFields - fields to exclude from validation
 */
function principalFieldsAreValid(form: RFQFormState, excludedFields: string[] = []) {
  const principalFields = getTypedKeys(form)
    .filter(k => form[k] instanceof BaseField)
    .filter(k => !excludedFields.includes((form[k] as BaseField<any, any>).name))
    .map(k => form[k]);

  const principalHasError = principalFields.some(f => (f as BaseField<any, any>).hasInvalidValue);
  return !principalHasError;
}

// Says if the user has selected an allocation or not
const selectAllocationsArePopulated = createSelector(
  (state: AppState) => state.rfq.form.allocations,
  allocations => {
    return allocations.at(0)?.subAccountField.value != null;
  }
);

const selectCanInitMarketsField = createSelector(
  selectAllocationsArePopulated,
  (state: AppState) => state.referenceData.settings.enableAccountSegregation,
  (allocationsPopulated, enableAccountSegregation) => {
    if (enableAccountSegregation) {
      return allocationsPopulated;
    }

    return true;
  }
);

const selectCanSendRfq = createSelector(
  selectCurrentCareOrder,
  (state: AppState) => state.rfq.isLoading,
  (state: AppState) => state.rfq.currentQuote,
  (state: AppState) => state.rfq.form,
  (state: AppState) => state.rfq.dependencies.tradableSubAccounts.length,
  (careOrder, isLoading, currentQuote, form, numTradableSubAccounts) => {
    // Check that care order has loaded
    const parentID = form.parentIDField.value;
    if (parentID != null && careOrder == null) {
      return false;
    }

    // Validate fields
    const hasValidAllocations = allocationsAreValid(form.allocations, numTradableSubAccounts);
    const hasValidPrincipalFields = principalFieldsAreValid(form);

    return currentQuote == null && !isLoading && hasValidAllocations && hasValidPrincipalFields;
  }
);

const selectQuantityCurrencyOptions = createSelector(
  (state: AppState) => state.rfq.form.symbolField.value,
  (security): QtyInputCurrencyOptions<RFQCurrency>[] => {
    if (!security) {
      return [];
    }

    return getRFQFormCurrencySpec(security).getAvailableCurrencies(security);
  }
);

const selectIsRFQFormCounterCurrency = createSelector(
  (state: AppState) => state.rfq.form.rfqCurrencyField.value,
  (state: AppState) => state.rfq.form.symbolField.value,
  (currency, security) => {
    if (currency === UNITS_CURRENCY) {
      return false;
    }

    return isCounterCurrency(currency, security);
  }
);

const selectRFQFormOrdType = createSelector(
  (state: AppState) => state.rfq.form.symbolField.value,
  (state: AppState) => state.rfq.form.pricingModeField.value,
  (security, pricingMode) => {
    if (!security) {
      return undefined;
    }

    return getOrdTypeForQuote(security, pricingMode);
  }
);

const selectAssignToParentToggleDisabled = createSelector(
  (state: AppState) => state.rfq.form.assignToParentField,
  (state: AppState) => state.rfq.currentCareOrder,
  (state: AppState) => state.rfq.currentQuote,
  (assignToParentField, currentCareOrder, currentQuote) => {
    if (currentCareOrder && currentCareOrder.isComplete) {
      // We can't toggle being assigned to the parent (the care order) if it is already completed
      return true;
    }

    if (currentQuote) {
      // When currentQuote is defined, it means that we are actively RFQing. We cannot toggle being assigned to the parent anymore.
      return true;
    }

    // Will always be false, but keeping the semantics correct by referencing the field's own disabled property
    return assignToParentField.isDisabled;
  }
);

/** All selectable securities in the RFQ Form. Used by symbol selectors that show all securities. */
const selectAllSelectableSecurities = createSelector(
  (state: AppState) => state.rfq.referenceData.securities.securitiesList,
  (state: AppState) => state.rfq.referenceData.settings.enableRFQFormFundSecurities,
  (state: AppState) => state.rfq.referenceData.settings.enableRFQFormOptions,
  (securities, enableRFQFormFundSecurities, enableRFQFormOptions) =>
    securities.filter(
      sec =>
        isSpot(sec) ||
        (enableRFQFormFundSecurities && isCompound(sec)) ||
        (enableRFQFormOptions && isFalconXOption(sec))
    )
);

const selectAvailableFunds = createSelector(
  (state: AppState) => state.rfq.referenceData.funds.fundsList,
  fundsList => fundsList.filter(isFundSelectableInRFQForm)
);

/** Selectable securities based on the currently selected product group. Used by symbol selectors shown after some product group is selected. */
const selectSelectableSecuritiesWithinProductGroup = createSelector(
  selectAllSelectableSecurities,
  (state: AppState) => state.rfq.productGroup,
  (state: AppState) => state.rfq.form.fundField.value,
  (state: AppState) => state.rfq.referenceData.funds.fundsBySymbol,
  (securities, productGroup, selectedFund, fundsBySymbol) => {
    let output = securities.filter(sec => getGroup(sec) === productGroup);

    if (selectedFund) {
      const fundDef = fundsBySymbol.get(selectedFund);
      const tradableSymbolsForFundSet = fundDef ? new Set(fundDef.TradableSecurities) : undefined;
      output = output.filter(sec => tradableSymbolsForFundSet?.has(sec.Symbol));
    }

    return output;
  }
);

/** Determines if the view should show the selector for the single fixing index case */
const selectIsSingleFixingSelectorVisible = createSelector(
  (state: AppState) => state.rfq.form.pricingModeField.value,
  (state: AppState) => state.rfq.form.symbolField.value,
  (pricingMode, security) => {
    if (pricingMode !== PricingModeEnum.SpreadToFixing) {
      return false;
    }

    const isMultiToken = security && SecurityTypesUtils.Compound.isType(security) && security.Legs.length > 1;
    if (isMultiToken) {
      // For multi token, everything is just read-only, and anyway you'd need to show some multi-select kind of component here
      // We don't support this yet in the view. But we will in time let the user change the index per leg in this case
      return false;
    }

    return true;
  }
);

/** Returns the tradable indices (names) by symbol Map<string, string[]> */
const selectAvailableIndicesBySymbol = createSelector(
  (state: AppState) => state.rfq.form.symbolField.value?.Symbol,
  (state: AppState) => state.rfq.referenceData.fixingIndices.tradableIndicesBySymbol,
  (symbol, tradableIndicesBySymbol): Map<string, string[]> => {
    if (!symbol) {
      return new Map();
    }

    return tradableIndicesBySymbol.get(symbol) ?? new Map();
  }
);

/** Simply grabs the Map<string, Position> map from the streaming data slice cache */
const selectPositionsDataMap = createSelector(
  (state: AppState) => state,
  state => streamingDataSlice.endpoints.getPositions.select(RFQSLICE_POSITION_REQUEST_PARAMS)(state).data
);

export const selectPositionInfo = createSelector(
  selectPositionsDataMap,
  (state: AppState) => state.rfq.form.symbolField.value,
  (state: AppState) => state.rfq.form.rfqMarketsField.selections,
  (positionsMap, security, selectedMarketAccounts): AggregatePositionInfo | undefined => {
    if (!security || !positionsMap) {
      return undefined;
    }

    const marketAccount = selectedMarketAccounts.at(0);
    if (!marketAccount) {
      return undefined;
    }

    const position = positionsMap.get(
      getPositionID(RFQSLICE_POSITION_REQUEST_PARAMS.Source, marketAccount, security.Symbol, undefined)
    );
    if (!position) {
      return undefined;
    }

    const positionAmount = getPositionAmount(position.Amount, security);
    return {
      currency: positionAmount.currency,
      positions: [
        {
          value: positionAmount.value,
          currency: positionAmount.currency,
          marketAccount,
          symbol: security.Symbol,
        },
      ],
    };
  }
);

/** Just returns a list of all the selected indices we have so far. Not necessarily ordered correctly (compacted) */
const selectSelectedIndices = createSelector(
  (state: AppState) => state.rfq.form.fixingField,
  fixingField => compact(fixingField.value?.map(f => f.index))
);

/** If there is a current care order and it is set to be used ("assigned"), then its Markets array is returned, if there is one. */
const selectCareOrderMarkets = createSelector(
  (state: AppState) => state.rfq.currentCareOrder,
  (state: AppState) => state.rfq.form.assignToParentField,
  (cco, assigned) => {
    return assigned.value ? cco?.Markets?.map(m => m.Market) : undefined;
  }
);

const selectMSSRequestParams = createSelector(
  (state: AppState) => state.rfq.form.symbolField.value,
  (state: AppState) => state.rfq.referenceData.settings.allowSyntheticCrosses,
  (security, allowSyntheticCrosses) => {
    if (!security) {
      return undefined;
    }

    const symbols = [security.Symbol];
    if (allowSyntheticCrosses) {
      const syntheticCrossSymbol = getRFQSyntheticCrossSymbol(security);
      syntheticCrossSymbol && symbols.push(syntheticCrossSymbol);
    }

    return {
      Symbols: symbols,
    };
  }
);

/** A selector which collects all the params to be used for building market selector items for the RFQ Form */
const selectBuildRFQMarketSelectorItemsParams = createSelector(
  [
    // Pieces of rfq/form state
    (state: AppState) => state.rfq.form.symbolField.value,
    (state: AppState) => state.rfq.form.allocations,
    (state: AppState) => state.rfq.form.rfqTradingProtocolField.value,
    (state: AppState) => state.rfq.form.pricingModeField.value,
    selectSelectedIndices,
    selectIsRFQFormCounterCurrency,
    selectCareOrderMarkets,

    // Various settings
    (state: AppState) => state.rfq.referenceData.settings.allowSyntheticCcy,
    (state: AppState) => state.rfq.referenceData.settings.allowSyntheticCrosses,
    (state: AppState) => state.rfq.referenceData.settings.enableAccountSegregation,
    (state: AppState) => state.rfq.referenceData.settings.enableTakerMarketplaceETFRFQFlow,

    // Ref data state
    (state: AppState) => state.rfq.referenceData.securities.securitiesBySymbol,
    (state: AppState) => state.rfq.referenceData.markets.marketsByName,
    (state: AppState) => state.rfq.referenceData.marketAccounts.marketAccountsByMarket,
    (state: AppState) => state.rfq.referenceData.marketAccounts.marketAccountsByName,
    (state: AppState) => state.rfq.referenceData.marketAccounts.marketAccountsList,
    (state: AppState) => state.rfq.referenceData.fixingIndices.marketsByFixingIndex,
    (state: AppState) => state.rfq.referenceData.strategies.strategiesByName,
    (state: AppState) => state.rfq.referenceData.marketAccountPermissionFilters.marketAccountFilterIDsBySubAccountName,
    (state: AppState) =>
      state.rfq.referenceData.marketAccountPermissionFilters.marketAccountPermissionFiltersByFilterID,
  ],
  (
    security,
    allocations,
    rfqTradingProtocol,
    pricingMode,
    selectedFixingIndices,
    isCounterCurrency,
    careOrderMarkets,

    allowSyntheticCrossCurrency,
    allowSyntheticCrosses,
    enableAccountSegregation,
    enableTakerMarketplaceETFRFQFlow,

    securitiesBySymbol,
    marketsByName,
    marketAccountsByMarket,
    marketAccountsByName,
    marketAccountsList,
    marketsByFixingIndex,
    strategiesByName,
    marketAccountFilterIDsBySubAccountName,
    marketAccountPermissionFiltersByFilterID
  ) => {
    return {
      security,
      symbol: security?.Symbol,

      subAccountAllocations: subAccountFieldsToAllocations(allocations),
      rfqTradingProtocol,
      pricingMode,
      fixingIndices: enableTakerMarketplaceETFRFQFlow ? selectedFixingIndices : undefined,
      usingCounterCurrency: isCounterCurrency,
      careOrderMarkets,

      allowSyntheticCrossCurrency,
      allowSyntheticCrosses,
      enableAccountSegregation,

      securitiesBySymbol,
      marketsByName,
      marketAccountsByMarket,
      marketAccountsByName,
      marketAccountsList,
      marketsByFixingIndex,
      strategiesByName,
      marketAccountFilterIDsBySubAccountName,
      marketAccountPermissionFiltersByFilterID,
    } satisfies Omit<GetRFQMarketSelectorItemsParams, 'mssByAccountBySymbol' | 'enableMSSValidation'>;
  }
);

export const rfqSelectors = {
  selectCurrentCareOrder,
  selectIsLoading,
  selectAllocationsArePopulated,
  selectCanInitMarketsField,
  selectCanSendRfq,
  selectQuantityCurrencyOptions,
  selectIsRFQFormCounterCurrency,
  selectCareOrderMarkets,
  selectMSSRequestParams,
  selectBuildRFQMarketSelectorItemsParams,
  selectRFQFormOrdType,
  selectAllSelectableSecurities,
  selectSelectableSecuritiesWithinProductGroup,
  selectPositionsDataMap,
  selectPositionInfo,
  selectIsSingleFixingSelectorVisible,
  selectAvailableIndicesBySymbol,
  selectAssignToParentToggleDisabled,
  selectAvailableFunds,
};
